import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { getValidToken } from '../auth/tokenManager';
import { BackendDataService } from '../services/BackendDataService';
import { NavigationHistoryService } from '../services/NavigationHistoryService';
import { isDemoMode } from '../services/demoMode';
import {
  fetchKpiTargets,
  indexTargets,
  resolveTarget,
  saveKpiTarget,
  type Kpi,
  type KpiScope,
  type KpiTargetRow,
  type KpiTargetValues,
} from '../services/KpiTargetService';
import type { 
  Machine, 
  Line, 
  Factory, 
  CustomerGroup, 
  Customer, 
  Region, 
  Country 
} from '../services/BackendDataService';

// Navigation history types
export interface CachedMachineData {
  maschinennummer?: string;
  maschineBK?: string;
  bautyp?: string;
  machineName?: string;
  machineType?: string;
  lineId?: string;
  lineName?: string;
  factoryName?: string;
  datenVerfuegbar?: boolean;
  cmVerfuegbar?: boolean;
}

export interface NavigationEvent {
  type: 'machine' | 'line';
  id: string;
  name: string;
  timestamp: number;
  sessionId: string;
  userId: string;
  // Optional cached machine data for instant loading
  machineData?: CachedMachineData;
}

/**
 * Lifecycle of the machine-park fetch, as the UI needs to see it.
 *
 * `idle`        nothing attempted yet (also after reset())
 * `loading`     a fetch is in flight
 * `ready`       we hold a usable machine list (possibly a stale one, if the last
 *               refresh failed but an earlier fetch had succeeded)
 * `empty`       the backend answered successfully with no machines
 * `unavailable` NATS not connected / no token — transient, retryable
 * `error`       the backend rejected the request or the request threw
 */
export type SAPDataStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'empty'
  | 'unavailable'
  | 'error';

// Store state interface
interface SAPDataState {
  // Raw SAP hierarchical data (source of truth) - ALL machines
  sapTreeData: Record<string, CustomerGroup>;
  
  // Machines with data only (from PC.machines_with_data)
  sapTreeDataWithData: Record<string, CustomerGroup>;
  
  // Pending data from background sync (to avoid re-renders)
  pendingSAPData: Record<string, CustomerGroup> | null;
  hasPendingData: boolean;
  
  // Transformed flat arrays (derived from sapTreeData) - ALL machines
  machines: Machine[];
  lines: Line[];
  factories: Factory[];
  
  // Transformed flat arrays - ONLY machines with data
  machinesWithData: Machine[];
  linesWithData: Line[];
  factoriesWithData: Factory[];
  
  // Performance optimization: pre-computed hierarchical context lookup
  hierarchicalContextCache: Map<string, {
    customer?: string;
    country?: string;
    region?: string;
    customerGroup?: string;
  }>;
  
  // Filter results cache for performance (search tags only, not data availability)
  filterCache: {
    key: string; // Hash of filter parameters
    machines: Machine[];
    lines: Line[];
    sapTreeData: Record<string, CustomerGroup>;
  } | null;
  sapTreeDataVersion: number; // Increment when sapTreeData changes
  
  // Navigation history (local state)
  navigationHistory: NavigationEvent[];
  currentUser: string | null;
  sessionId: string;
  
  // Loading and error states
  loading: boolean;
  error: string | null;
  // Two deliberately separate signals:
  //  - `lastFetched` means "we hold a definitive machine list" and is set only
  //    on the `ok` and `empty` outcomes. It also drives the 1-hour cache check
  //    in fetchSAPData, so setting it on a failure would cache that failure for
  //    an hour and suppress the next non-forced fetch.
  //  - `dataStatus` means "a fetch has settled" and is set on EVERY outcome,
  //    including failures. The UI gates on this one; gating on `lastFetched` is
  //    exactly what made the overview spin forever when a fetch failed.
  lastFetched: Date | null;
  dataStatus: SAPDataStatus;
  
  // Filter states
  showOnlyWithData: boolean;
  searchTags: string[];
  searchOperators: ('AND' | 'OR')[]; // Individual operators between tags
  
  // HomeView state persistence
  homeViewState: {
    activeTab: string;
    viewMode: 'cards' | 'tree' | 'globe';
    expandedNodes: Set<string>;
  };
  
  // Global date range state
  dateRange: {
    start: string;
    end: string;
  };
  isDatePickerOpen: boolean;
  
  // KPI targets of the organization (machine, line, machine type)
  kpiTargets: KpiTargetRow[];
  kpiTargetIndex: Map<string, KpiTargetRow>;
  kpiTargetsLoaded: boolean;
  
  // Actions
  fetchSAPData: (token: string | null, force?: boolean) => Promise<void>;
  /**
   * Settle the view as unavailable without a fetch having been attempted.
   *
   * Needed because `dataStatus` only leaves `idle` when a fetch runs, and a
   * fetch only runs once NATS is connected. If the websocket never comes up
   * there is nothing to report the failure, and the overview sits on its
   * spinner indefinitely — the original bug, reached by a different route.
   * No-ops unless the status is still `idle`, so it can never overwrite a real
   * answer that arrived in the meantime.
   */
  markDataUnavailable: (message: string) => void;
  setPendingData: (data: Record<string, CustomerGroup>) => void;
  applyPendingData: () => void;
  setShowOnlyWithData: (value: boolean) => void;
  setSearchTags: (tags: string[]) => void;
  setSearchOperator: (index: number, operator: 'AND' | 'OR') => void; // Set individual operator
  
  // Date range actions
  setDateRange: (startDate: string, endDate: string) => void;
  setDatePickerOpen: (isOpen: boolean) => void;
  resetDateRange: () => void;
  
  // HomeView state actions
  setHomeViewState: (state: { activeTab?: string; viewMode?: 'cards' | 'tree' | 'globe'; expandedNodes?: Set<string> }) => void;
  getHomeViewState: () => { activeTab: string; viewMode: 'cards' | 'tree' | 'globe'; expandedNodes: Set<string> };
  
  hasValidData: () => boolean;
  forceRefresh: (token: string | null) => Promise<void>;
  
  // Performance optimization methods
  buildHierarchicalContextCache: () => void;
  getHierarchicalContext: (machineId: string, lineId?: string) => {
    customer?: string;
    country?: string;
    region?: string;
    customerGroup?: string;
  };
  
  // Search helper
  matchesSearchTags: (searchTags: string[], itemName: string, itemType?: string, factoryName?: string, hierarchicalContext?: {
    customer?: string;
    country?: string;
    region?: string;
    customerGroup?: string;
  }, oemNumbers?: string[]) => boolean;
  
  // Getters for filtered data
  getFilteredMachines: () => Machine[];
  getFilteredLines: () => Line[];
  getFilteredFactories: () => Factory[];
  getFilteredSAPTreeData: () => Record<string, CustomerGroup>;
  
  // Utility methods
  getMachineById: (id: string) => Machine | undefined;
  getLineById: (id: string) => Line | undefined;
  getFactoryByName: (name: string) => Factory | undefined;
  getMachinesInLine: (lineId: string) => Machine[];
  getLinesInFactory: (factoryName: string) => Line[];
  
  // Navigation history methods
  setCurrentUser: (userId: string | null) => void;
  recordVisit: (type: 'machine' | 'line', id: string, name: string) => void;
  getNavigationHistory: () => NavigationEvent[];
  setNavigationHistory: (history: NavigationEvent[]) => void;
  getCachedMachineById: (id: string) => CachedMachineData | undefined;
  clearNavigationHistory: () => void;
  
  // KPI targets (KpiTargetService): loaded once per session
  fetchKpiTargets: (token: string) => Promise<void>;
  /** Replaces the targets of one machine, line or machine type; no target clears them. */
  saveKpiTarget: (scope: KpiScope, scopeId: string, targets: KpiTargetValues, token: string) => Promise<void>;
  /** The target the charts draw: the machine's own, else its line's, else its machine type's. */
  getTargetForMachine: (machine: Machine, targetType: Kpi) => number | null;
  
  // Reset method
  reset: () => void;
}

// Create the store
export const useSAPDataStore = create<SAPDataState>()(
  subscribeWithSelector((set, get) => ({
    // Initial state - ALL machines
    sapTreeData: {},
    machines: [],
    lines: [],
    factories: [],
    // Initial state - machines WITH DATA only
    sapTreeDataWithData: {},
    machinesWithData: [],
    linesWithData: [],
    factoriesWithData: [],
    // Pending data from background sync
    pendingSAPData: null,
    hasPendingData: false,
    // Caches and metadata
    hierarchicalContextCache: new Map(),
    filterCache: null,
    sapTreeDataVersion: 0,
    navigationHistory: [],
    currentUser: null,
    sessionId: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    loading: false,
    error: null,
    lastFetched: null,
    dataStatus: 'idle',
    showOnlyWithData: false,
    searchTags: [],
    searchOperators: [], // Default to empty array
    
    kpiTargets: [],
    kpiTargetIndex: new Map(),
    kpiTargetsLoaded: false,
    
    // Initialize HomeView state
    homeViewState: {
      activeTab: 'Recently Viewed', // Default tab
      viewMode: 'tree', // Default view mode
      expandedNodes: new Set<string>() // Default to no expanded nodes
    },
    
    // Initialize date range - check localStorage first, then default to last year
    dateRange: (() => {
      // Try to restore from localStorage
      const savedDateRange = localStorage.getItem('pc-date-range');
      if (savedDateRange) {
        try {
          const parsed = JSON.parse(savedDateRange);
          if (parsed.start && parsed.end) {
            console.log('📅 Store: Restored date range from localStorage:', parsed);
            return parsed;
          }
        } catch (e) {
          console.warn('📅 Store: Failed to parse saved date range:', e);
        }
      }
      
      // Default to last year (365 days ago to today)
      const today = new Date();
      const lastYear = new Date(today);
      lastYear.setDate(today.getDate() - 365);
      
      return {
        start: lastYear.toISOString().slice(0, 10),
        end: today.toISOString().slice(0, 10)
      };
    })(),
    isDatePickerOpen: false,

    // Navigation history methods
    setCurrentUser: (userId: string | null) => {
      set({ currentUser: userId });
    },

    recordVisit: (type: 'machine' | 'line', id: string, name: string) => {
      // Demo mode keeps no per-user trail: nothing in memory, nothing persisted.
      if (isDemoMode()) return;

      const { currentUser, sessionId, navigationHistory, getMachineById } = get();
      
      if (!currentUser) {
        console.warn(`❌ Store: Cannot record ${type} visit for "${name}" (${id}) - no current user set`);
        return;
      }

      // Cache machine data for instant loading on future visits
      let machineData: CachedMachineData | undefined;
      if (type === 'machine') {
        const machine = getMachineById(id);
        if (machine) {
          machineData = {
            maschinennummer: machine.maschinennummer,
            maschineBK: machine.maschineBK,
            bautyp: machine.bautyp,
            machineName: machine.machineName,
            machineType: machine.machineType,
            lineId: machine.lineId,
            lineName: (machine as any).lineName,
            factoryName: machine.factoryName,
            datenVerfuegbar: machine.datenVerfuegbar,
            cmVerfuegbar: machine.cmVerfuegbar
          };
        }
      }

      const newEvent: NavigationEvent = {
        type,
        id,
        name,
        timestamp: Date.now(),
        sessionId,
        userId: currentUser,
        machineData
      };

      // Remove any existing entry for the same item to avoid duplicates
      const filteredHistory = navigationHistory.filter(
        event => !(event.type === type && event.id === id)
      );

      // Add new event at the beginning (most recent first)
      const updatedHistory = [newEvent, ...filteredHistory].slice(0, 20); // Keep only last 20 items
      
      set({ navigationHistory: updatedHistory });

      // Trigger NATS sync in background (non-blocking)
      NavigationHistoryService.recordVisit(type, id, name, currentUser, sessionId, machineData).catch(error => {
        console.error('Failed to sync navigation event to NATS:', error);
      });
    },

    getNavigationHistory: () => {
      const { navigationHistory } = get();
      return [...navigationHistory]; // Return a copy to prevent mutations
    },

    setNavigationHistory: (history: NavigationEvent[]) => {
      console.log(`🏪 Store: Setting navigation history (${history.length} items)`);
      set({ navigationHistory: [...history] });
    },

    clearNavigationHistory: () => {
      set({ navigationHistory: [] });
    },

    // Get cached machine data from navigation history (for instant loading)
    getCachedMachineById: (id: string) => {
      const { navigationHistory } = get();
      const event = navigationHistory.find(
        e => e.type === 'machine' && (e.id === id || e.machineData?.maschinennummer === id || e.machineData?.maschineBK === id)
      );
      return event?.machineData;
    },

    markDataUnavailable: (message: string) => {
      if (get().dataStatus !== 'idle') return;
      console.warn('🔍 Store: no fetch possible, settling as unavailable:', message);
      set({ loading: false, error: message, dataStatus: 'unavailable' });
    },

    // Fetch SAP data and transform it
    fetchSAPData: async (token: string | null, force = false) => {
      const state = get();
      
      // Prevent multiple simultaneous fetch operations
      if (state.loading) {
        console.log('🔍 Store: Fetch already in progress, ignoring duplicate request');
        return;
      }

      // Since SAP data changes only weekly, extend cache time to 1 hour (unless forced)
      const cacheTimeMs = 60 * 60 * 1000; // 1 hour
      if (!force && state.lastFetched && (Date.now() - state.lastFetched.getTime()) < cacheTimeMs) {
        console.log('🔍 Store: Using cached SAP data (less than 1 hour old)');
        return;
      }

      // Try to get a fresh token; fall back to the passed-in one
      let freshToken = token;
      try {
        freshToken = await getValidToken();
      } catch {
        // getValidToken may throw if not authenticated yet (e.g. during init)
      }

      if (!freshToken) {
        // Settled, but with no answer about the park: transient and retryable.
        set({ 
          loading: false, 
          error: 'No authentication token available. Please log in.',
          dataStatus: 'unavailable'
        });
        return;
      }

      console.log(`🔍 Store: Fetching SAP data (force: ${force}, existing data: ${Object.keys(state.sapTreeData).length > 0 ? 'yes' : 'no'})`);
      set({ loading: true, error: null, dataStatus: 'loading' });

      try {
        // Fetch all machines in a single request
        console.log('🔍 Store: Calling BackendDataService.fetchSAPData...');
        const __fetchStart = performance.now();
        const result = await BackendDataService.fetchSAPData(freshToken);
        console.log(`⏱️ [PERF] Store: fetchSAPData (network+parse) total=${(performance.now() - __fetchStart).toFixed(0)}ms`);
        console.log('🔍 Store: Received fetch result, status:', result.status);

        // An empty park is a valid answer, not a failure. It used to throw here,
        // which left `lastFetched` null and the overview on a spinner forever.
        if (result.status === 'empty') {
          console.log('🔍 Store: Backend reported an empty machine park');
          set((state) => ({
            sapTreeData: {},
            machines: [],
            lines: [],
            factories: [],
            sapTreeDataWithData: {},
            machinesWithData: [],
            linesWithData: [],
            factoriesWithData: [],
            loading: false,
            error: null,
            dataStatus: 'empty',
            lastFetched: new Date(),
            sapTreeDataVersion: state.sapTreeDataVersion + 1,
            filterCache: null,
            hierarchicalContextCache: new Map()
          }));
          return;
        }

        if (result.status !== 'ok') {
          // `unavailable` / `error`: no definitive park list, so `lastFetched`
          // stays untouched. If we are already holding data we keep showing it
          // and stay `ready` (a failed refresh must not blank a working
          // overview); otherwise the UI gets a real failure state to render.
          const hasExistingData = Object.keys(state.sapTreeData).length > 0;
          const isUnavailable = result.status === 'unavailable';

          if (hasExistingData) {
            console.log('🔍 Store: Keeping existing data, fetch did not succeed');
            set({
              loading: false,
              error: isUnavailable
                ? 'Backend services temporarily unavailable. Using cached data.'
                : `Data refresh failed: ${result.message}. Using cached data.`,
              dataStatus: 'ready'
            });
          } else {
            set({
              loading: false,
              error: isUnavailable
                ? 'Backend services are currently unavailable. Please check your connection or try again later.'
                : result.message,
              dataStatus: isUnavailable ? 'unavailable' : 'error'
            });
          }
          return;
        }

        const sapTreeData = result.data;

        const __transformStart = performance.now();

        // Propagate totalMachineCount to all line nodes
        let __t = performance.now();
        propagateTotalMachineCountToTree(sapTreeData);
        console.log(`⏱️ [PERF] Store: propagateTotalMachineCount=${(performance.now() - __t).toFixed(0)}ms`);
        
        // Filter tree for machines with data (client-side filtering instead of second API call)
        // Note: filterTreeForMachinesWithData preserves original totalMachineCount
        __t = performance.now();
        const sapTreeDataWithData = filterTreeForMachinesWithData(sapTreeData);
        console.log(`⏱️ [PERF] Store: filterTreeForMachinesWithData=${(performance.now() - __t).toFixed(0)}ms`);
        
        // Transform to flat arrays - ALL machines
        __t = performance.now();
        const machines = BackendDataService.transformSAPDataToMachines(sapTreeData);
        const lines = BackendDataService.transformSAPDataToLines(sapTreeData);
        const factories = BackendDataService.transformSAPDataToFactories(sapTreeData);
        console.log(`⏱️ [PERF] Store: transform ALL (machines/lines/factories)=${(performance.now() - __t).toFixed(0)}ms`);
        
        console.log(`🔍 Store: Transformed - ${machines.length} machines, ${lines.length} lines, ${factories.length} factories`);

        // Transform to flat arrays - ONLY machines with data (derived from filtered tree)
        __t = performance.now();
        const machinesWithData = BackendDataService.transformSAPDataToMachines(sapTreeDataWithData);
        const linesWithData = BackendDataService.transformSAPDataToLines(sapTreeDataWithData);
        const factoriesWithData = BackendDataService.transformSAPDataToFactories(sapTreeDataWithData);
        console.log(`⏱️ [PERF] Store: transform WITH-DATA=${(performance.now() - __t).toFixed(0)}ms`);
        console.log(`⏱️ [PERF] Store: all transforms total=${(performance.now() - __transformStart).toFixed(0)}ms`);
        
        console.log(`🔍 Store: With data - ${machinesWithData.length} machines, ${linesWithData.length} lines`);

        set((state) => ({
          // All machines
          sapTreeData: sapTreeData,
          machines: machines,
          lines: lines,
          factories: factories,
          // Machines with data only (derived client-side)
          sapTreeDataWithData: sapTreeDataWithData,
          machinesWithData: machinesWithData,
          linesWithData: linesWithData,
          factoriesWithData: factoriesWithData,
          // Metadata
          loading: false,
          error: null,
          // The service reports `empty` from "no customer groups", but a tree
          // can carry groups and still contain no machines once flattened
          // (any empty level in groups → regionen → … → maschinen yields []).
          // Decide it here on the transformed count instead, so "I have no
          // machines" always reaches the empty state rather than rendering
          // section headers reading (0).
          dataStatus: machines.length === 0 ? 'empty' : 'ready',
          lastFetched: new Date(),
          sapTreeDataVersion: state.sapTreeDataVersion + 1,
          filterCache: null // Invalidate filter cache
        }));
        
        console.log('✅ Store: SAP data successfully stored');
        
        // Build hierarchical context cache for performance optimization
        const __cacheStart = performance.now();
        get().buildHierarchicalContextCache();
        console.log(`⏱️ [PERF] Store: buildHierarchicalContextCache=${(performance.now() - __cacheStart).toFixed(0)}ms`);
        
      } catch (error) {
        console.error('🔍 Store: Failed to fetch SAP data:', error);
        
        // Check if this is a NATS 503 error (backend not available)
        const isBackendUnavailable = error instanceof Error && 
          (error.message.includes('503') || 
           error.message.includes('NATS connection not available') ||
           error.message.includes('NATS connection lost'));
        
        // If we have existing data, keep it and just update error state
        if (Object.keys(state.sapTreeData).length > 0) {
          console.log('🔍 Store: Keeping existing data due to fetch error');
          const errorMessage = isBackendUnavailable 
            ? 'Backend services temporarily unavailable. Using cached data.'
            : `Data refresh failed: ${error instanceof Error ? error.message : 'Unknown error'}. Using cached data.`;
          
          set({ 
            loading: false, 
            error: errorMessage,
            // Still `ready`: the cached list on screen is usable, and `error`
            // carries the reason the refresh failed.
            dataStatus: 'ready'
          });
        } else {
          // No existing data, provide helpful message based on error type
          const errorMessage = isBackendUnavailable
            ? 'Backend services are currently unavailable. Please check your connection or try again later.'
            : error instanceof Error ? error.message : 'Failed to fetch SAP data';
          
          set({ 
            loading: false, 
            error: errorMessage,
            // An exception sniffed as a backend outage is reported as
            // `unavailable` (transient, worth retrying) to stay consistent with
            // the message above; anything else is a genuine `error`.
            dataStatus: isBackendUnavailable ? 'unavailable' : 'error'
          });
        }
        // `lastFetched` is deliberately NOT set on any failure path — see the
        // comment on the field declaration. `dataStatus` is the settled signal.
      }
    },

    // Helper to check if store has valid data
    hasValidData: () => {
      const state = get();
      return state.sapTreeData && 
             Object.keys(state.sapTreeData).length > 0 && 
             state.machines.length > 0 && 
             !state.error;
    },

    // Set pending data from background sync (doesn't trigger re-renders)
    setPendingData: (data: Record<string, CustomerGroup>) => {
      set({ pendingSAPData: data, hasPendingData: true });
    },

    // Apply pending data to the store (call this when navigating to overview)
    applyPendingData: () => {
      const { pendingSAPData } = get();
      if (!pendingSAPData) return;

      console.log('🔄 Store: Applying pending data...');

      // Propagate totalMachineCount to all line nodes
      propagateTotalMachineCountToTree(pendingSAPData);
      
      // Filter tree for machines with data (client-side filtering)
      // Note: filterTreeForMachinesWithData preserves original totalMachineCount
      const sapTreeDataWithData = filterTreeForMachinesWithData(pendingSAPData);
      
      // Transform to flat arrays - ALL machines
      const machines = BackendDataService.transformSAPDataToMachines(pendingSAPData);
      const lines = BackendDataService.transformSAPDataToLines(pendingSAPData);
      const factories = BackendDataService.transformSAPDataToFactories(pendingSAPData);
      
      // Transform to flat arrays - ONLY machines with data
      const machinesWithData = BackendDataService.transformSAPDataToMachines(sapTreeDataWithData);
      const linesWithData = BackendDataService.transformSAPDataToLines(sapTreeDataWithData);
      const factoriesWithData = BackendDataService.transformSAPDataToFactories(sapTreeDataWithData);

      set((state) => ({
        sapTreeData: pendingSAPData,
        machines: machines,
        lines: lines,
        factories: factories,
        sapTreeDataWithData: sapTreeDataWithData,
        machinesWithData: machinesWithData,
        linesWithData: linesWithData,
        factoriesWithData: factoriesWithData,
        pendingSAPData: null,
        hasPendingData: false,
        lastFetched: new Date(),
        // pendingSAPData is only ever assigned from a non-empty `ok` payload in
        // SAPDataProvider's background sync, so `ready` holds unconditionally.
        dataStatus: 'ready',
        sapTreeDataVersion: state.sapTreeDataVersion + 1,
        filterCache: null,
      }));

      // Build hierarchical context cache
      get().buildHierarchicalContextCache();
      console.log('✅ Store: Pending data applied');
    },

    // Force refresh method (can be called manually)
    forceRefresh: async (token: string | null) => {
      console.log('🔄 Store: Force refresh requested');
      await get().fetchSAPData(token, true);
    },

    // Set filter state
    setShowOnlyWithData: (value: boolean) => {
      set({ showOnlyWithData: value, filterCache: null });
    },

    // Set search tags
    setSearchTags: (tags: string[]) => {
      set({ 
        searchTags: tags,
        filterCache: null, // Invalidate cache on filter change
        // Ensure we have operators for each pair of tags (n-1 operators for n tags)
        searchOperators: tags.length > 1 ? 
          get().searchOperators.slice(0, tags.length - 1).concat(
            Array(Math.max(0, tags.length - 1 - get().searchOperators.length)).fill('AND')
          ) : []
      });
    },

    // Set individual search operator between tags
    setSearchOperator: (index: number, operator: 'AND' | 'OR') => {
      const { searchOperators } = get();
      const newOperators = [...searchOperators];
      newOperators[index] = operator;
      set({ searchOperators: newOperators, filterCache: null });
    },

    // Date range actions
    setDateRange: (startDate: string, endDate: string) => {
      console.log(`📅 Store: Setting global date range: ${startDate} to ${endDate}`);
      const dateRange = { start: startDate, end: endDate };
      
      // Persist to localStorage
      try {
        localStorage.setItem('pc-date-range', JSON.stringify(dateRange));
        console.log('📅 Store: Date range saved to localStorage');
      } catch (e) {
        console.warn('📅 Store: Failed to save date range to localStorage:', e);
      }
      
      set({ dateRange });
    },

    setDatePickerOpen: (isOpen: boolean) => {
      console.log(`📅 Store: Setting date picker ${isOpen ? 'open' : 'closed'}`);
      set({ isDatePickerOpen: isOpen });
    },

    resetDateRange: () => {
      const today = new Date();
      const lastYear = new Date(today);
      lastYear.setDate(today.getDate() - 365);
      
      const startDate = lastYear.toISOString().slice(0, 10);
      const endDate = today.toISOString().slice(0, 10);
      
      console.log(`📅 Store: Resetting date range to last year: ${startDate} to ${endDate}`);
      
      // Clear from localStorage
      try {
        localStorage.removeItem('pc-date-range');
      } catch (e) {
        console.warn('📅 Store: Failed to clear date range from localStorage:', e);
      }
      
      set({ 
        dateRange: { start: startDate, end: endDate }
      });
    },

    // HomeView state actions
    setHomeViewState: (state: { activeTab?: string; viewMode?: 'cards' | 'tree' | 'globe'; expandedNodes?: Set<string> }) => {
      const currentState = get().homeViewState;
      const newState = {
        activeTab: state.activeTab ?? currentState.activeTab,
        viewMode: state.viewMode ?? currentState.viewMode,
        expandedNodes: state.expandedNodes ?? currentState.expandedNodes
      };
      
      set({ homeViewState: newState });
    },

    getHomeViewState: () => {
      const { homeViewState } = get();
      return homeViewState;
    },

    // Build hierarchical context cache for performance optimization
    buildHierarchicalContextCache: () => {
      const { sapTreeData } = get();
      const cache = new Map<string, {
        customer?: string;
        country?: string;
        region?: string;
        customerGroup?: string;
      }>();

      Object.values(sapTreeData).forEach(customerGroup => {
        customerGroup.regionen?.forEach(region => {
          region.laender?.forEach(country => {
            country.kunden?.forEach(customer => {
              customer.werke?.forEach(factory => {
                factory.linien?.forEach(line => {
                  const hierarchicalContext = {
                    customer: customer.kundenName,
                    country: country.land,
                    region: region.region,
                    customerGroup: customerGroup.kundengruppe
                  };

                  // Cache context for line
                  cache.set(`line:${line.linieBK}`, hierarchicalContext);

                  // Cache context for each machine in the line
                  line.maschinen?.forEach(machine => {
                    const machineKey = machine.maschineBK || machine.maschinennummer;
                    if (machineKey) {
                      cache.set(`machine:${machineKey}`, hierarchicalContext);
                    }
                  });
                });
              });
            });
          });
        });
      });

      set({ hierarchicalContextCache: cache });
    },

    // Get hierarchical context from cache (fast lookup)
    getHierarchicalContext: (machineId: string, lineId?: string) => {
      const { hierarchicalContextCache } = get();
      
      // Try machine lookup first
      const machineContext = hierarchicalContextCache.get(`machine:${machineId}`);
      if (machineContext) {
        return machineContext;
      }

      // Fallback to line lookup if provided
      if (lineId) {
        const lineContext = hierarchicalContextCache.get(`line:${lineId}`);
        if (lineContext) {
          return lineContext;
        }
      }

      // Return empty context if not found
      return {};
    },

    // Helper function to match search tags against item data with hierarchical context
    matchesSearchTags: (searchTags: string[], itemName: string, itemType?: string, factoryName?: string, hierarchicalContext?: {
      customer?: string;
      country?: string;
      region?: string;
      customerGroup?: string;
    }, oemNumbers?: string[]) => {
      if (searchTags.length === 0) return true;
      if (searchTags.length === 1) {
        // Single tag, simple match
        const searchText = [
          itemName,
          itemType,
          factoryName,
          hierarchicalContext?.customer,
          hierarchicalContext?.country,
          hierarchicalContext?.region,
          hierarchicalContext?.customerGroup,
          ...(oemNumbers || [])
        ].filter(Boolean).join(' ').toLowerCase();
        
        return searchText.includes(searchTags[0].toLowerCase());
      }
      
      const { searchOperators } = get();
      
      // Build comprehensive search text including hierarchical context
      const searchText = [
        itemName,
        itemType,
        factoryName,
        hierarchicalContext?.customer,
        hierarchicalContext?.country,
        hierarchicalContext?.region,
        hierarchicalContext?.customerGroup,
        ...(oemNumbers || [])
      ].filter(Boolean).join(' ').toLowerCase();
      
      // Improved search logic: Apply operators left-to-right for better intuitive behavior
      // Example: "BAT AND 350 OR 550" evaluates as "(BAT AND 350) OR 550"
      
      // First, check which tags match
      const tagMatches = searchTags.map(tag => searchText.includes(tag.toLowerCase()));
      
      // Apply operators left to right
      let result = tagMatches[0];
      
      for (let i = 0; i < searchOperators.length; i++) {
        const operator = searchOperators[i] || 'AND';
        const nextTagMatch = tagMatches[i + 1];
        
        if (operator === 'AND') {
          result = result && nextTagMatch;
        } else { // OR
          result = result || nextTagMatch;
        }
      }
      
      return result;
    },

    // Get filtered machines - uses backend-filtered data based on toggle
    getFilteredMachines: () => {
      const { machines, machinesWithData, showOnlyWithData, searchTags, searchOperators, sapTreeDataVersion, filterCache, matchesSearchTags, getHierarchicalContext } = get();
      
      // Generate cache key
      const cacheKey = `${sapTreeDataVersion}-${showOnlyWithData}-${searchTags.join(',')}-${searchOperators.join(',')}`;
      
      // Return cached result if available
      if (filterCache && filterCache.key === cacheKey && filterCache.machines && filterCache.machines.length > 0) {
        return filterCache.machines;
      }
      
      // Select the right dataset based on toggle (backend already filtered)
      // Toggle ON = show all machines, Toggle OFF = show only machines with data
      let filtered = showOnlyWithData ? machines : machinesWithData;
      
      // Apply search tags filter with hierarchical context (only if there are search tags)
      if (searchTags.length > 0) {
        filtered = filtered.filter(machine => {
          // Get hierarchical context from cache (fast O(1) lookup)
          const machineId = machine.maschineBK || machine.maschinennummer;
          const hierarchicalContext = getHierarchicalContext(machineId, machine.lineId);
          
          return matchesSearchTags(
            searchTags, 
            machine.machineName || machine.bautyp || machine.maschinennummer || '',
            machine.bautyp,
            machine.factoryName,
            hierarchicalContext
          );
        });
      }
      
      // Update cache
      const newCache = {
        key: cacheKey,
        machines: filtered,
        lines: filterCache?.lines || [],
        sapTreeData: filterCache?.sapTreeData || {}
      };
      setTimeout(() => {
        const currentState = get();
        if (!currentState.filterCache || currentState.filterCache.key !== cacheKey) {
          set({ filterCache: newCache });
        }
      }, 0);
      
      return filtered;
    },

    // Get filtered lines - uses backend-filtered data based on toggle
    getFilteredLines: () => {
      const { lines, linesWithData, showOnlyWithData, searchTags, searchOperators, sapTreeDataVersion, filterCache, matchesSearchTags, getHierarchicalContext } = get();
      
      // Generate cache key
      const cacheKey = `${sapTreeDataVersion}-${showOnlyWithData}-${searchTags.join(',')}-${searchOperators.join(',')}`;
      
      // Return cached result if available
      if (filterCache && filterCache.key === cacheKey && filterCache.lines && filterCache.lines.length > 0) {
        return filterCache.lines;
      }
      
      // Select the right dataset based on toggle (backend already filtered)
      // Toggle ON = show all lines, Toggle OFF = show only lines with data
      let filtered = showOnlyWithData ? lines : linesWithData;
      
      // Apply search tags filter with hierarchical context (only if there are search tags)
      if (searchTags.length > 0) {
        filtered = filtered.filter(line => {
          // Get hierarchical context from cache (fast O(1) lookup)
          const hierarchicalContext = getHierarchicalContext('', line.linieBK);
          
          // Build search text including linieNummer for line number search
          const linieNummer = line.linieNummer || '';
          const lineSearchText = [line.linieName, line.linieBK, linieNummer].filter(Boolean).join(' ');
          
          // OEM number is on the line (not on machines)
          const lineOemNumbers = line.oemNummer ? [line.oemNummer] : undefined;
          
          return matchesSearchTags(
            searchTags,
            lineSearchText,
            'line',
            line.factoryName,
            hierarchicalContext,
            lineOemNumbers
          );
        });
      }
      
      // Update cache
      setTimeout(() => {
        const currentState = get();
        if (currentState.filterCache && currentState.filterCache.key === cacheKey) {
          set({ filterCache: { ...currentState.filterCache, lines: filtered } });
        }
      }, 0);
      
      return filtered;
    },

    // Get filtered factories - uses backend-filtered data based on toggle
    getFilteredFactories: () => {
      const { factories, factoriesWithData, showOnlyWithData } = get();
      // Select the right dataset based on toggle (backend already filtered)
      return showOnlyWithData ? factories : factoriesWithData;
    },

    // Get filtered SAP tree data for tree view - uses backend-filtered data based on toggle
    getFilteredSAPTreeData: () => {
      const { sapTreeData, sapTreeDataWithData, showOnlyWithData, searchTags, searchOperators, sapTreeDataVersion, filterCache, matchesSearchTags, getHierarchicalContext } = get();
      
      // Generate cache key
      const cacheKey = `${sapTreeDataVersion}-${showOnlyWithData}-${searchTags.join(',')}-${searchOperators.join(',')}`;
      
      // Return cached result if available
      if (filterCache && filterCache.key === cacheKey && filterCache.sapTreeData && Object.keys(filterCache.sapTreeData).length > 0) {
        return filterCache.sapTreeData;
      }
      
      // Select the right dataset based on toggle (backend already filtered for data availability)
      // Toggle ON = all machines, Toggle OFF = only machines with data
      const sourceData = showOnlyWithData ? sapTreeData : sapTreeDataWithData;
      
      // OPTIMIZATION: If no search tags, return source data directly (no clone needed)
      if (searchTags.length === 0) {
        setTimeout(() => {
          const currentState = get();
          if (!currentState.filterCache || currentState.filterCache.key !== cacheKey) {
            set({ filterCache: { key: cacheKey, machines: [], lines: [], sapTreeData: sourceData } });
          }
        }, 0);
        return sourceData;
      }
      
      // Only need to filter for search tags - use structuredClone (faster than JSON.parse/stringify)
      const filteredData = typeof structuredClone === 'function' 
        ? structuredClone(sourceData)
        : JSON.parse(JSON.stringify(sourceData));
        
      Object.keys(filteredData).forEach(groupKey => {
        const customerGroup = filteredData[groupKey];
        customerGroup.regionen = customerGroup.regionen?.map((region: Region) => ({
          ...region,
          laender: region.laender?.map((country: Country) => ({
            ...country,
            kunden: country.kunden?.map((customer: Customer) => ({
              ...customer,
              werke: customer.werke?.map((factory: Factory) => ({
                ...factory,
                linien: factory.linien?.map((line: Line) => {
                  // Get hierarchical context from cache (fast O(1) lookup)
                  const hierarchicalContext = getHierarchicalContext('', line.linieBK);
                  
                  // OEM number is on the line (not on machines)
                  const lineOemNumbers = line.oemNummer ? [line.oemNummer] : undefined;
                  
                  // Check if line matches search - include linieNummer for line number search
                  const linieNummer = line.linieNummer || '';
                  const lineText = `${line.linieName || ''} ${line.linieBK || ''} ${linieNummer} line`;
                  const lineMatches = matchesSearchTags(searchTags, lineText, 'line', factory.werkName, hierarchicalContext, lineOemNumbers);
                  
                  let filteredMachines: Machine[];
                  if (lineMatches) {
                    // If line matches search: show ALL machines in that line
                    filteredMachines = line.maschinen || [];
                  } else {
                    // If line doesn't match: filter machines by search terms
                    filteredMachines = line.maschinen?.filter(machine => {
                      const machineText = `${machine.machineName || ''} ${machine.bautyp || ''} ${machine.maschinennummer || ''}`;
                      return matchesSearchTags(searchTags, machineText, machine.bautyp, factory.werkName, hierarchicalContext);
                    }) || [];
                  }
                  
                  return {
                    ...line,
                    maschinen: filteredMachines
                  };
                }).filter((line: Line) => line.maschinen.length > 0) || []
              })).filter((factory: Factory) => factory.linien.length > 0) || []
            })).filter((customer: Customer) => customer.werke.length > 0) || []
          })).filter((country: Country) => country.kunden.length > 0) || []
        })).filter((region: Region) => region.laender.length > 0) || [];
      });

      // Remove empty customer groups
      Object.keys(filteredData).forEach(groupKey => {
        if (filteredData[groupKey].regionen?.length === 0) {
          delete filteredData[groupKey];
        }
      });

      // Update cache
      setTimeout(() => {
        const currentState = get();
        if (!currentState.filterCache || currentState.filterCache.key !== cacheKey) {
          set({ filterCache: { key: cacheKey, machines: [], lines: [], sapTreeData: filteredData } });
        }
      }, 0);

      return filteredData;
    },

    // Utility methods
    getMachineById: (id: string) => {
      const { machines } = get();
      return machines.find(machine => 
        machine.maschineBK === id || 
        machine.maschinennummer === id ||
        machine.bautyp === id ||
        machine.machineType === id
      );
    },

    getLineById: (id: string) => {
      const { lines } = get();
      return lines.find(line => line.linieBK === id);
    },

    getFactoryByName: (name: string) => {
      const { factories } = get();
      return factories.find(factory => factory.werkName === name);
    },

    getMachinesInLine: (lineId: string) => {
      const { machines } = get();
      return machines.filter(machine => machine.lineId === lineId);
    },

    getLinesInFactory: (factoryName: string) => {
      const { lines } = get();
      return lines.filter(line => line.factoryName === factoryName);
    },

    // KPI targets of the organization (cockpitservice, Doris kpi_targets).
    // The demo dataset has none (its account has no cockpitservice).
    fetchKpiTargets: async (token: string) => {
      if (isDemoMode()) {
        set({ kpiTargets: [], kpiTargetIndex: new Map(), kpiTargetsLoaded: true });
        return;
      }
      try {
        const rows = await fetchKpiTargets(token);
        set({ kpiTargets: rows, kpiTargetIndex: indexTargets(rows), kpiTargetsLoaded: true });
      } catch (error) {
        // the charts then draw no target line; the next load tries again
        console.warn('⚠️ KPI targets not loaded:', error);
      }
    },

    saveKpiTarget: async (scope, scopeId, targets, token) => {
      if (isDemoMode()) throw new Error('Targets are read-only in demo mode');
      await saveKpiTarget(scope, scopeId, targets, token);
      const others = get().kpiTargets.filter((r) => !(r.scope === scope && r.scopeId === scopeId));
      const rows = Object.keys(targets).length > 0 ? [...others, { scope, scopeId, targets }] : others;
      set({ kpiTargets: rows, kpiTargetIndex: indexTargets(rows) });
    },

    getTargetForMachine: (machine: Machine, targetType: Kpi) => {
      const subject = {
        machineSn: machine.maschinennummer || machine.maschineBK,
        lineBk: machine.lineId,
        machineType: machine.bautyp || machine.machineType,
      };
      return resolveTarget(get().kpiTargetIndex, subject, targetType)?.value ?? null;
    },

    // Reset store to initial state
    reset: () => {
      set({
        sapTreeData: {},
        sapTreeDataWithData: {},
        machines: [],
        machinesWithData: [],
        lines: [],
        linesWithData: [],
        factories: [],
        factoriesWithData: [],
        hierarchicalContextCache: new Map(),
        filterCache: null,
        sapTreeDataVersion: 0,
        navigationHistory: [],
        currentUser: null,
        sessionId: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        loading: false,
        error: null,
        lastFetched: null,
        dataStatus: 'idle',
        showOnlyWithData: false,
        searchTags: [],
        searchOperators: [],
        kpiTargets: [],
        kpiTargetIndex: new Map(),
        kpiTargetsLoaded: false,
        homeViewState: {
          activeTab: 'Recently Viewed',
          viewMode: 'tree',
          expandedNodes: new Set<string>()
        },
        dateRange: {
          start: new Date().toISOString().slice(0, 10),
          end: new Date().toISOString().slice(0, 10)
        },
        isDatePickerOpen: false
      });
    }
  }))
);

// Utility to propagate totalMachineCount to all line nodes in the SAP tree
type MutableLine = Line & { maschinen: Machine[]; totalMachineCount?: number };
function propagateTotalMachineCountToTree(tree: Record<string, CustomerGroup>) {
  Object.values(tree).forEach((customerGroup) => {
    customerGroup.regionen?.forEach((region) => {
      region.laender?.forEach((country) => {
        country.kunden?.forEach((customer) => {
          customer.werke?.forEach((factory) => {
            factory.linien?.forEach((line: MutableLine) => {
              line.totalMachineCount = Array.isArray(line.maschinen) ? line.maschinen.length : 0;
            });
          });
        });
      });
    });
  });
}

// Filter tree to only include machines with datenVerfuegbar=true
// Preserves totalMachineCount from original tree (unfiltered count)
function filterTreeForMachinesWithData(tree: Record<string, CustomerGroup>): Record<string, CustomerGroup> {
  const filtered: Record<string, CustomerGroup> = {};
  
  Object.entries(tree).forEach(([key, customerGroup]) => {
    const filteredRegionen = customerGroup.regionen
      ?.map(region => ({
        ...region,
        laender: region.laender
          ?.map(country => ({
            ...country,
            kunden: country.kunden
              ?.map(customer => ({
                ...customer,
                werke: customer.werke
                  ?.map(factory => ({
                    ...factory,
                    linien: factory.linien
                      ?.map(line => {
                        // Preserve original totalMachineCount before filtering
                        const originalTotal = line.totalMachineCount ?? line.maschinen?.length ?? 0;
                        return {
                          ...line,
                          totalMachineCount: originalTotal,
                          maschinen: line.maschinen?.filter(m => m.datenVerfuegbar) || []
                        };
                      })
                      .filter(line => line.maschinen.length > 0) || []
                  }))
                  .filter(factory => factory.linien.length > 0) || []
              }))
              .filter(customer => customer.werke.length > 0) || []
          }))
          .filter(country => country.kunden.length > 0) || []
      }))
      .filter(region => region.laender.length > 0) || [];
    
    if (filteredRegionen.length > 0) {
      filtered[key] = { ...customerGroup, regionen: filteredRegionen };
    }
  });
  
  return filtered;
}

// Selectors for common use cases
export const selectMachines = (state: SAPDataState) => state.machines;
export const selectLines = (state: SAPDataState) => state.lines;
export const selectFactories = (state: SAPDataState) => state.factories;
export const selectSAPTreeData = (state: SAPDataState) => state.sapTreeData;
export const selectFilteredMachines = (state: SAPDataState) => state.getFilteredMachines();
export const selectFilteredLines = (state: SAPDataState) => state.getFilteredLines();
export const selectFilteredFactories = (state: SAPDataState) => state.getFilteredFactories();
export const selectFilteredSAPTreeData = (state: SAPDataState) => state.getFilteredSAPTreeData();
